import { useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_POLL_INTERVAL_MS = 7000;

export const usePublicAuctionView = ({
  tournamentId,
  fetcher,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  errorMessage = 'Unable to load public auction view.',
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const requestSequence = useRef(0);

  useEffect(() => {
    if (!tournamentId || typeof fetcher !== 'function') {
      setLoading(false);
      return undefined;
    }

    let mounted = true;
    const load = async ({ silent = false } = {}) => {
      const requestId = ++requestSequence.current;
      try {
        if (!silent && mounted) setLoading(true);
        const response = await fetcher(tournamentId);
        if (!mounted || requestId !== requestSequence.current) return;
        setData(response || null);
        setError('');
        setIsStale(false);
        setLastUpdatedAt(new Date());
      } catch (err) {
        if (!mounted || requestId !== requestSequence.current) return;
        setError(err?.message || errorMessage);
        setIsStale(true);
      } finally {
        if (!silent && mounted && requestId === requestSequence.current) setLoading(false);
      }
    };

    load();
    const intervalId = setInterval(() => load({ silent: true }), pollIntervalMs);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [errorMessage, fetcher, pollIntervalMs, tournamentId]);

  const auction = data?.auction || {};
  const teams = useMemo(() => (Array.isArray(data?.teams) ? data.teams : []), [data?.teams]);
  const players = useMemo(() => (Array.isArray(data?.players) ? data.players : []), [data?.players]);
  const soldPlayers = useMemo(() => players.filter((player) => player.status === 'sold'), [players]);

  return {
    data,
    loading,
    error,
    auction,
    teams,
    players,
    soldPlayers,
    lastUpdatedAt,
    isStale,
    pollIntervalMs,
  };
};

export default usePublicAuctionView;
